import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Error } from "../../../../components/common/Error";
import { AdminActionCreator } from "../../../../store/Admin/actions";
import { API_ENDPOINTS } from "../../../../constants/common";
import { apiClient } from "../../../../utils";

import {
    selectIsAuth,

    selectUsersPending,
    selectUsersError,
    selectUsersErrorMessage,
    selectUsersList,
    selectUsersTotal,
    selectUsersPage,
    selectUsersPerPage,
    selectUsersLastPage,
    selectUsersNextUrl,
    selectUsersPrevUrl,
    selectUsersSearch,
    selectUsersSort,
    selectUsersOrder,
} from "../../../../store/selectors";

import { SearchBar } from "../SearchBar";
import { EmptyState } from "../EmptyState";
import { LoadingState } from "../LoadingState";
import { UsersTable } from "../UsersTable";
import {SortKey} from "Realty/pages/ControlPanel/types";
import { pickListingsCount } from "../../utils/user";

type ApiOrder = "asc" | "desc";

const mapUiSortKeyToApiSort = (k: SortKey): string => {
    if (k === "createdAt") return "created_at";
    if (k === "listingsCount") return "listings_count";
    if (k === "name") return "name";
    if (k === "email") return "email";
    // для "users" expiresAt не используем, но на всякий:
    return "created_at";
};

const mapApiSortToUiSortKey = (apiSort?: string | null): SortKey => {
    if (apiSort === "created_at") return "createdAt";
    if (apiSort === "listings_count") return "listingsCount";
    if (apiSort === "name") return "name";
    if (apiSort === "email") return "email";
    return "createdAt";
};

const handleCreateOrEditSubscription = (user: any) => {
    // TODO: открыть модалку "Подписка" (создать/продлить/изменить)
    console.log("Edit subscription for user:", user?.id, user);
};

const LISTING_COUNT_PAGE_SIZE = 100;

const loadListingCount = async (userId: number | string): Promise<number | null> => {
    let cursor: string | null = null;
    let count = 0;
    const visitedCursors = new Set<string>();

    try {
        do {
            const params = new URLSearchParams({
                limit: String(LISTING_COUNT_PAGE_SIZE),
                include_unpublished: "true",
            });
            if (cursor) params.set("cursor", cursor);
            const response = await apiClient.get(`${API_ENDPOINTS.classifiedsAuthorListings(userId)}?${params.toString()}`);
            const pageItems = Array.isArray(response?.data?.data) ? response.data.data : [];
            count += pageItems.length;

            const pagination = response?.data?.meta?.pagination;
            const nextCursor = typeof pagination?.next_cursor === "string" ? pagination.next_cursor : null;
            const hasMore = Boolean(pagination?.has_more) && Boolean(nextCursor) && !visitedCursors.has(nextCursor);
            if (nextCursor) visitedCursors.add(nextCursor);
            cursor = hasMore ? nextCursor : null;
        } while (cursor);

        return count;
    } catch {
        return null;
    }
};

export const _UsersPanel = ({
                                isAuth,

                                pending,
                                error,
                                errorMessage,

                                users,
                                total,
                                page,
                                perPage,
                                lastPage,
                                nextUrl,
                                prevUrl,

                                // сохранённые параметры запроса из state (чтобы при возврате на вкладку UI был консистентен)
                                savedSearch,
                                savedSort,
                                savedOrder,

                                getUsers,
                            }: any) => {
    // локальный UI-стейт
    const [search, setSearch] = useState<string>(savedSearch || "");
    const [sortKey, setSortKey] = useState<SortKey>(mapApiSortToUiSortKey(savedSort));
    const [sortDir, setSortDir] = useState<"asc" | "desc">((savedOrder as any) || "desc");
    const skipInitialFilterFetchRef = useRef(true);
    const nextPageRequestRef = useRef(false);
    const nextPageSentinelRef = useRef<HTMLDivElement | null>(null);
    const listingCountRequestsRef = useRef(new Set<string>());
    const [listingCounts, setListingCounts] = useState<Record<string, number>>({});

    // Первый фетч. Следующие страницы подгружаются наблюдателем внизу списка.
    useEffect(() => {
        if (!isAuth) return;

        getUsers({
            page,
            perPage,
            search,
            sort: mapUiSortKeyToApiSort(sortKey),
            order: sortDir as ApiOrder,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuth, perPage]);

    // debounce для поиска/сортировки (чтобы не долбить сервер на каждую букву)
    useEffect(() => {
        if (!isAuth) return;
        if (skipInitialFilterFetchRef.current) {
            skipInitialFilterFetchRef.current = false;
            return;
        }

        const t = window.setTimeout(() => {
            getUsers({
                page: 1,
                perPage,
                search,
                sort: mapUiSortKeyToApiSort(sortKey),
                order: sortDir as ApiOrder,
            });
        }, 350);

        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, sortKey, sortDir, isAuth]);

    const canLoadMore = !!nextUrl && !pending;

    useEffect(() => {
        const usersWithoutCount = (Array.isArray(users) ? users : []).filter((user: any) => {
            const id = user?.id;
            return id != null && pickListingsCount(user) === null && !listingCountRequestsRef.current.has(String(id));
        });
        if (!usersWithoutCount.length) return;

        let cancelled = false;
        const queue = [...usersWithoutCount];
        const worker = async () => {
            while (!cancelled && queue.length) {
                const user = queue.shift();
                const id = String(user?.id);
                if (!id) continue;
                listingCountRequestsRef.current.add(id);
                const count = await loadListingCount(user.id);
                if (!cancelled && count !== null) {
                    setListingCounts((current) => ({ ...current, [id]: count }));
                }
            }
        };

        void Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
        return () => { cancelled = true; };
    }, [users]);

    const usersWithListingCounts = useMemo(() => {
        const list = (Array.isArray(users) ? users : []).map((user: any) => {
            const id = String(user?.id ?? "");
            const loadedCount = listingCounts[id];
            return loadedCount === undefined ? user : { ...user, listings_count: loadedCount };
        });

        if (sortKey !== "listingsCount") return list;
        const direction = sortDir === "asc" ? 1 : -1;
        return [...list].sort(
            (left, right) => ((pickListingsCount(left) ?? -1) - (pickListingsCount(right) ?? -1)) * direction,
        );
    }, [listingCounts, sortDir, sortKey, users]);

    const loadNextPage = useCallback(() => {
        if (!canLoadMore || nextPageRequestRef.current) return;
        nextPageRequestRef.current = true;
        getUsers({
            page: page + 1,
            perPage,
            search,
            sort: mapUiSortKeyToApiSort(sortKey),
            order: sortDir as ApiOrder,
        });
    }, [canLoadMore, getUsers, page, perPage, search, sortDir, sortKey]);

    useEffect(() => {
        if (!pending) nextPageRequestRef.current = false;
    }, [pending]);

    useEffect(() => {
        const target = nextPageSentinelRef.current;
        if (!target || !canLoadMore) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) loadNextPage();
        }, { rootMargin: "320px 0px" });

        observer.observe(target);
        return () => observer.disconnect();
    }, [canLoadMore, loadNextPage]);

    // В users-режиме срок подписки не является полем сортировки.
    const safeSortKey: SortKey = useMemo(() => {
        return sortKey === "expiresAt" ? "createdAt" : sortKey;
    }, [sortKey]);

    if (!isAuth) {
        return (
            <Error
                title="Требуется авторизация"
                description="Войдите в аккаунт, чтобы просматривать пользователей."
            />
        );
    }

    if (pending && (usersWithListingCounts?.length ?? 0) === 0) {
        return <LoadingState title="Загрузка пользователей..." />;
    }

    if (error && (usersWithListingCounts?.length ?? 0) === 0) {
        return (
            <Error
                title="Не удалось загрузить пользователей"
                description={errorMessage || "Попробуйте обновить страницу"}
            />
        );
    }

    return (
        <div className="w-full">
            {/* Search */}
            <div className="mt-4">
                <SearchBar value={search} onChange={setSearch} />
            </div>

            {/* Table */}
            <div className="mt-4">
                {(usersWithListingCounts?.length ?? 0) === 0 && !pending ? (
                    <EmptyState />
                ) : (
                    <UsersTable
                        variant="users"
                        title="Все пользователи"
                        users={usersWithListingCounts}
                        page={page}
                        lastPage={lastPage}
                        total={total}
                        pending={pending}
                        actionsDisabled={pending}
                        sortKey={safeSortKey}
                        sortDir={sortDir}
                        onChangeSortKey={(k) => setSortKey(k === "expiresAt" ? "createdAt" : k)}
                        onToggleDir={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
                        onCreateSubscription={handleCreateOrEditSubscription}
                    />
                )}
            </div>

            <div ref={nextPageSentinelRef} className="flex min-h-14 items-center justify-center py-5">
                {pending && (usersWithListingCounts?.length ?? 0) > 0 ? <span className="text-sm text-slate-500">Загружаем ещё пользователей…</span> : null}
                {!pending && nextUrl ? <button type="button" onClick={loadNextPage} className="text-sm font-semibold text-[#8b2a5e] hover:text-[#6e2048]">Показать ещё</button> : null}
                {!pending && !nextUrl && (usersWithListingCounts?.length ?? 0) > 0 ? <span className="text-sm text-slate-400">Все пользователи загружены</span> : null}
            </div>

            {error && (usersWithListingCounts?.length ?? 0) > 0 ? (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Ошибка обновления: {errorMessage || "Unknown error"}
                </div>
            ) : null}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    isAuth: selectIsAuth(state),

    pending: selectUsersPending(state),
    error: selectUsersError(state),
    errorMessage: selectUsersErrorMessage(state),

    users: selectUsersList(state),
    total: selectUsersTotal(state),

    page: selectUsersPage(state),
    perPage: selectUsersPerPage(state),
    lastPage: selectUsersLastPage(state),

    nextUrl: selectUsersNextUrl(state),
    prevUrl: selectUsersPrevUrl(state),

    savedSearch: selectUsersSearch(state),
    savedSort: selectUsersSort(state),
    savedOrder: selectUsersOrder(state),
});

const mapDispatchToProps = {
    getUsers: AdminActionCreator.getUsers,
};

export const UsersPanel = connect(mapStateToProps, mapDispatchToProps)(_UsersPanel);
